import { NavMenu } from '@shopify/app-bridge-react';
import { isMobile, isShopifyEmbedded } from 'utils/appBridgeEnv';
import { AppProvider as PolarisProvider, Button, Text } from '@shopify/polaris';
import enTranslations from '@shopify/polaris/locales/en.json';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RouterLink from '@/shared/links/RouterLink';
import WholePageLoading from '@/shared/loaders/WholePageLoading';
import '@shopify/polaris/build/esm/styles.css';
import 'assets/sass/app.scss';
import { PATHS } from 'config/constants';
import { useCanAddCampaign } from 'hooks/useCanAddCampaign';
import { useChatwoot } from 'hooks/useChatwoot';
import { usePermissions } from 'hooks/usePermissions';
import { useShopInfo } from 'hooks/useShopInfo';

import { useShopInfoCrossTabSync } from 'hooks/useShopInfoCrossTabSync';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Link } from 'react-router';
import { setApiAppBridge } from 'rest/dist/utils/fetchInstance';
import { getPlansAction } from 'states/actions/plans';
import { getSettingAction } from 'states/actions/setting';
import ScrollToTop from '@/shared/ScrollToTop';
import Router from './Router';
import { api } from './api';
import { getDashboardBootstrapAction, getShopInfoAction } from './states/actions/shop';
import { loadScript } from './utils/loadScript';
import { perfMark } from './utils/perfTrace';

const withEmbeddedParams = (path) => {
  const sp = new URLSearchParams(window.location.search);
  const host = sp.get('host');
  const shop = sp.get('shop');
  const [p, q] = String(path).split('?');
  const next = new URLSearchParams(q || '');
  if (host && !next.get('host')) next.set('host', host);
  if (shop && !next.get('shop')) next.set('shop', shop);
  const qs = next.toString();
  return qs ? `${p}?${qs}` : p;
};

// Loaded lazily (below, after mount) rather than at module scope — nothing
// in the app currently reads a flag from it (no useFeature/isOn/
// getFeatureValue calls anywhere), so there's no flicker risk in deferring
// its ~1.9MB SDK off the LCP-critical path. GrowthBookProvider itself comes
// from the same dynamic import, so until it resolves the tree just renders
// without a provider (React.Fragment) — functionally identical today since
// nothing consumes the context.
function buildGrowthbook(GrowthBook) {
  return new GrowthBook({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: 'sdk-XMfFOBpXD7Nvb6vF',
    subscribeToChanges: true,
    trackingCallback: (experiment, result) => {},
  });
}

// Fires once, the moment this module is evaluated (i.e. as soon as the bundle
// containing App.js has downloaded and started executing) — the earliest
// checkpoint in the [PageLoad][PERF] trail, so every later mark's timestamp
// can be read as "Xms after the app's own JS started running", not just
// "Xms after navigation".
perfMark('App.js module evaluated');

const App = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const shopInfo = useShopInfo();
  useShopInfoCrossTabSync();
  const permissions = usePermissions();

  const [gb, setGb] = useState(null); // { Provider, instance } once loaded

  useEffect(() => {
    let cancelled = false;
    import('@growthbook/growthbook-react').then(({ GrowthBook, GrowthBookProvider }) => {
      if (cancelled) return;
      const instance = buildGrowthbook(GrowthBook);
      instance.loadFeatures();
      setGb({ Provider: GrowthBookProvider, instance });
    });
    return () => { cancelled = true; };
  }, []);

  const [isLoggedInAsUser, setIsLoggedInAsUser] = useState(false);
  // Live chat: loads the Chatwoot widget immediately, then identifies the
  // merchant (identifier_hash + store attributes from GET /api/chatwoot/
  // identity) once shopInfo has a real authenticated shop. Skipped on the
  // support-login/impersonation path so agents never get identified as the
  // merchant they're looking at. Same pattern as Prime MOQ.
  useChatwoot(!isLoggedInAsUser && Boolean(shopInfo?.data?.shopify_domain));
  // Set only if app-bridge.js fails or hangs past the timeout below. Without
  // this, a failed/stuck script load left getShopInfoAction() never firing,
  // so loadingShopInfo/loadingPermissions stayed true forever — the app sat
  // on WholePageLoading (or, if the failure happened even earlier, a blank
  // page) with zero feedback, recoverable only by the merchant guessing to
  // hit refresh. Reported more often on Safari (flakier third-party script
  // loading under ITP, extensions, etc.) but not Safari-specific in cause.
  const [bootstrapError, setBootstrapError] = useState(false);
  const loadingShopInfo = useSelector((state) => state?.shop?.loadingShopInfo);
  const loadingPermissions = useSelector((state) => state?.shop?.loadingPermissions);
  const canAddCampaign = useCanAddCampaign();
  // Fires once the shopInfo/permissions gate (below, `loadingShopInfo ||
  // loadingPermissions`) actually clears — this is the moment Router (and
  // the Dashboard's lazy chunk import) is first allowed to mount. Nothing
  // logged this before, so it was impossible to tell how much of the LCP
  // delay was "waiting on this gate" vs "everything after it".
  useEffect(() => {
    if (!loadingShopInfo && !loadingPermissions) {
      perfMark('shopInfo/permissions gate cleared (Router can now mount)');
    }
  }, [loadingShopInfo, loadingPermissions]);
  useEffect(() => {
    perfMark('App.js bootstrap effect start');
    // window.top !== window.self
    if (isMobile() || isShopifyEmbedded()) {
      const withTimeout = (promise, ms) =>
        Promise.race([
          promise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Timed out loading Shopify App Bridge script')), ms)
          ),
        ]);

      const scriptLoadStart = performance.now();
      console.log('[App.js][PERF] loading app-bridge.js ->', navigator.userAgent);
      withTimeout(loadScript('https://cdn.shopify.com/shopifycloud/app-bridge.js'), 12000)
        .then(() => {
          console.log(
            `[App.js][PERF] app-bridge.js loaded in ${(performance.now() - scriptLoadStart).toFixed(0)}ms`
          );
          const location = window.location;

          const queryParams = new URLSearchParams(location.search);
          // const queryParams = qs.parse(location.search.replace('?', ''));
          const host = queryParams.get('host');
          const shopDomain = queryParams.get('shop');
          // const locale = queryParams.get('locale');
          // 2026-07 migration: App Bridge v3 (`createApp`/`initAppBridge`,
          // classic `initializeWebVitals(app)`) removed entirely — the app
          // now runs on v4 only (CDN script loaded in public/index.html,
          // window.shopify global). Web Vitals reporting to Shopify's Partner
          // Dashboard is handled by the v4 script itself once loaded; there is
          // no equivalent app-side call needed to opt in (v4's
          // `shopify.webVitals.onReport(callback)` is only for forwarding
          // metrics to your OWN server, which this app doesn't currently do).
          setApiAppBridge({ host, shopDomain });
          perfMark('App.js: firing getDashboardBootstrapAction');
          // Replaces what used to be 3 separate calls (getShopInfoAction,
          // getDasboardReport, checkHasAnyOrdersAction — each firing its own
          // request(s)) with ONE request to /api/dashboard/bootstrap that
          // returns everything together. Those individual requests competing
          // for the browser's ~6-connections-per-origin budget (HTTP/1.1) in
          // the exact window the dashboard's LCP is gated on was a measured
          // contributor to slow first paint (web-vitals attribution showed
          // ~2.5s of elementRenderDelay against ~110ms TTFB — almost all
          // JS-side waiting on these requests, not network). Dispatches the
          // same Redux action types the individual calls did, so nothing
          // downstream (Dashboard.js's seeding from state.shop.*, or any
          // other consumer) needed to change. Dashboard.js still fires its
          // own on-mount copies of getDasboardReport/checkHasAnyOrders
          // (unchanged) for a revisit within the same session, but seeds its
          // initial state from whatever's already in Redux so it doesn't
          // re-block behind a skeleton if this already resolved first.
          getDashboardBootstrapAction(dispatch)(host);
        })
        .catch((error) => {
          console.error(
            `[App.js][PERF] app-bridge.js FAILED after ${(performance.now() - scriptLoadStart).toFixed(0)}ms ->`,
            error
          );
          setBootstrapError(true);
        });
    } else {
      try {
        // let isInstalled = false;
        const location = window.location;
        const queryParams = new URLSearchParams(window.location.search);
        // const host = queryParams.get('host');
        const shopDomain = queryParams.get('shop');
        if (shopDomain) {
          // Managed install + token exchange: there is no app-side OAuth route
          // anymore. Always open the app embedded in admin; Shopify runs the
          // managed install if the shop hasn't installed yet, and token exchange
          // then authenticates the embedded session.
          const shopnewdomain = shopDomain.replace('.myshopify.com', '');
          // Use the per-environment app handle (dev/stage/prod each set their
          // own REACT_APP_SHOPIFY_APP_SLUG) — hardcoding the prod handle sent
          // dev/stage stores to the wrong app's "not installed" page.
          location.href =
            'https://admin.shopify.com/store/' +
            shopnewdomain +
            '/apps/' +
            process.env.REACT_APP_SHOPIFY_APP_SLUG;
        } else {
          setIsLoggedInAsUser(true);
          api.setAppBridge('', '', true);
          try {
            const location = window.location;

            const queryParams = new URLSearchParams(window.location.search);
          } catch (error) {}
          const token = sessionStorage.getItem('loggedInAsUserToken');
          const isLoginPage = window.location.pathname.includes('/support/login');
          if (!isLoginPage) {
            if (token) {
              getShopInfoAction(dispatch)();
            } else {
              window.open(window.location.origin + PATHS.LOGIN, '_self');
            }
          } else {
            getShopInfoAction(dispatch)('', true);
          }
        }
      } catch (err) {
        console.error(err);
      }
    }
  }, [dispatch]);

  const config = {
    apiKey: process.env.REACT_APP_API_KEY,
    host: shopInfo?.host,
    forceRedirect: false,
  };

  useEffect(() => {
    const apiKey = process.env.REACT_APP_API_KEY; // Get from .env
    if (apiKey) {
      let metaTag = document.querySelector('meta[name="shopify-api-key"]');
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = 'shopify-api-key';
        document.head.appendChild(metaTag);
      }
      metaTag.content = apiKey;
    }
  }, []);

  // Neither plans nor settings are needed for the dashboard's first paint —
  // waiting on loadingShopInfo (rather than firing unconditionally on every
  // mount, [shopInfo]'s previous behavior since shopInfo is already `{}` on
  // the very first render) does two things: (1) it was firing BEFORE App
  // Bridge finished initializing (that happens inside the OTHER bootstrap
  // effect's loadScript().then()), sending a tokenless request that always
  // got a 403 + wasted an ~800ms reauth-check round trip for nothing — see
  // fetchInstance's hasShopifyApp:false log; (2) it removes 2 of the ~6-7
  // requests competing for the browser's ~6-connections-per-origin budget
  // (HTTP/1.1) during the exact window the dashboard's LCP is gated on.
  useEffect(() => {
    if (loadingShopInfo) return;
    getPlansAction(dispatch)();
    getSettingAction(dispatch)();
  }, [loadingShopInfo, dispatch]);

  useEffect(() => {
    const shopData = {
      shop_domain: shopInfo?.data?.shopify_domain,
      user_id: shopInfo?.data?.id,
      id: shopInfo?.data?.intercom_contact_id,
      email: shopInfo?.data?.customer_email,
      Phone: shopInfo?.data?.phone,
      owner: shopInfo?.data?.owner,
      name: shopInfo?.data?.shop_owner,
      'Contact Email': shopInfo?.data?.contact_email,
      'Company website': shopInfo?.data?.domain,
      'Company ID': shopInfo?.data?.id,

      'Company name': shopInfo?.data?.name,
      'Company Shopify Domain': shopInfo?.data?.shopify_domain,
      'Company Shopify Plan': shopInfo?.data?.shopify_plan,
    };
    if (isShopifyEmbedded()) {
      if (shopInfo?.data?.shopify_domain && gb) {
        const created_at = new Date(shopInfo?.data?.created_at).getTime();
        gb.instance.setAttributes({
          name: shopInfo?.data?.name,
          shopify_domain: shopInfo?.data?.shopify_domain,
          created_at,
        });
      }

      window._loq = window._loq || [];
      window._loq.push(['custom', { app: 'discount-prime-test', ...shopData }]);
      try {
        if (window.hj) {
          window.hj('identify', shopData.id, {
            name: shopData.shopDomain,
            plan: shopData.shopify_plan,
          });
        }
      } catch (error) {}
    } else {
      window._loq = window._loq || [];
      window._loq.push(['custom', { app: 'discount-prime-test', ...shopData }]);
    }
  }, [shopInfo, gb]);

  // Passthrough (no-op) wrapper until the deferred GrowthBook SDK resolves —
  // safe because nothing in the app currently reads a flag from its context.
  const GbWrapper = gb ? gb.Provider : React.Fragment;
  const gbWrapperProps = gb ? { growthbook: gb.instance } : {};

  // PolarisProvider now wraps the bootstrap gate too (it used to mount only
  // AFTER the gate cleared): WholePageLoading is a Polaris SkeletonPage and
  // needs the provider context, and keeping one stable provider across
  // gate→app avoids re-mounting the whole Polaris style/theme layer at the
  // exact moment the LCP element is trying to paint.
  if (bootstrapError) {
    return (
      <PolarisProvider i18n={enTranslations} theme={'light'}>
        <div
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', height: '100vh', gap: 12, padding: 24,
            textAlign: 'center',
          }}
        >
          <Text variant="headingMd" as="h2">Couldn't load the app</Text>
          <Text as="p" tone="subdued">
            This can happen on a slow connection or if a browser extension blocked a script.
          </Text>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </PolarisProvider>
    );
  }

  if (loadingShopInfo || loadingPermissions) {
    return (
      <PolarisProvider i18n={enTranslations} theme={'light'}>
        <WholePageLoading />
      </PolarisProvider>
    );
  }

  return (
    <>
      <GbWrapper {...gbWrapperProps}>
        <PolarisProvider i18n={enTranslations} theme={'light'} linkComponent={RouterLink}>
          {isLoggedInAsUser ? (
            <BrowserRouter>
              <ScrollToTop />
              <Router />
            </BrowserRouter>
          ) : (
            <BrowserRouter>
              <ScrollToTop />
              <AppContent host={shopInfo?.host} />
              <NavMenu>
                {[
                  {
                    label: t('analytics'),
                    destination: PATHS.ANALYTICS,
                  },
                  {
                    label: t('campaigns'),
                    destination: PATHS.CAMPAIGNS,
                  },
                  {
                    label: t('widgets'),
                    destination: PATHS.WIDGETS,
                  },
                  {
                    label: t('subscription_plans'),
                    destination: PATHS.PRICING,
                  },
                  {
                    label: t('settings'),
                    destination: PATHS.SETTING,
                  },
                  {
                    label: t('su.pageTitle'),
                    destination: PATHS.SAFE_UNINSTALL,
                  },
                  {
                    label: t('d.dashboardFooterLink.helpCenter'),
                    destination: PATHS.HELP,
                  },
                ].map((item, index) => (
                  <Link to={withEmbeddedParams(item.destination)} rel={item.label} key={index}>
                    {item.label}
                  </Link>
                ))}
              </NavMenu>

              <Router />
            </BrowserRouter>
          )}
        </PolarisProvider>
      </GbWrapper>
    </>
  );
};

export default App;

function AppContent({ host }) {
  // 2026-07 migration: this effect used to force a v3 App Bridge handshake
  // via initAppBridge(host).getState() and discarded the result — the app is
  // v4-only now (see the bootstrap effect above), which needs no equivalent
  // "warm the connection" call. Component kept as a no-op render to avoid
  // touching its call site below.
  return null;
}
